import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";

import { useContextCommentsSearchInfiniteQuery } from "hooks/queries/useContextCommentsQuery";
import type {
  ContextCommentKind,
  ContextCommentOwnerScope,
} from "lib/api/contextComments";
import type { Comment } from "util/constants";
import { getTaskUiStrings, resolveTaskLocale } from "i18n/taskUi";

/**
 * Picker props.
 */
export interface ContextCommentPickerProps {
  kind: ContextCommentKind;
  /** Chooses how the search is scoped. Defaults to `mixed`. */
  ownerScope?: ContextCommentOwnerScope;
  /** Fired when the user picks one of the existing comments. */
  onPick: (comment: Comment) => void;
  /**
   * Fired when the user elects to write a new principle/task. The host
   * form decides whether to open an inline composer, a modal, etc.
   * `seed` is the current search text so the user doesn't retype it.
   */
  onCreateNew: (seed: { content: string }) => void;
  /** When false, hides the "Write a new {kind}" affordance. */
  allowCreate?: boolean;
  /** Custom placeholder override; falls back to locale strings. */
  placeholder?: string;
  /**
   * Optionally hide comment ids that are already attached to the host
   * post so users can't double-link. Host forms pass this list.
   */
  excludeCommentIds?: Array<number | string>;
  /**
   * Restrict results to comments whose `properties.privacy` is in this
   * list. Typically `['public','anonymous']` when the host post is
   * anonymous, so personal/private rows are not offered for reuse.
   */
  audiences?: string[];
  className?: string;
}

/** Distance in pixels from the bottom of the scroll container at which
 * the picker will request the next page. Slightly lenient to avoid the
 * user hitting a hard stop before more rows arrive. */
const SCROLL_THRESHOLD_PX = 40;

/**
 * A compact "search existing + create new" combobox used by post forms
 * to pick which user-owned principles (or tasks) to attach to a solution
 * or action. All copy is localized via `i18n/taskUi`.
 *
 * Behavior:
 *   - Renders the caller's own matching rows on top, then a globally
 *     visible pool (public + anonymous by default) below.
 *   - Infinite-scrolls the global pool: each time the user scrolls
 *     close to the bottom of the dropdown we request the next page.
 *   - Accepts an `audiences` filter that constrains BOTH branches --
 *     anonymous host forms pass `['public','anonymous']` so a private
 *     row of the caller's own never appears.
 *
 * This component is intentionally stateless about which items were
 * selected -- hosts own that state.
 */
const ContextCommentPicker: React.FC<ContextCommentPickerProps> = ({
  kind,
  ownerScope = "mixed",
  onPick,
  onCreateNew,
  allowCreate = true,
  placeholder,
  excludeCommentIds = [],
  audiences,
  className = "",
}) => {
  const labels = useMemo(() => getTaskUiStrings(resolveTaskLocale()), []);
  const [rawQuery, setRawQuery] = useState<string>("");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(rawQuery), 250);
    return () => window.clearTimeout(handle);
  }, [rawQuery]);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (event.target instanceof Node && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isOpen]);

  const excludedIdSet = useMemo(
    () =>
      new Set(
        (Array.isArray(excludeCommentIds) ? excludeCommentIds : [])
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      ),
    [excludeCommentIds]
  );

  const searchQuery = useContextCommentsSearchInfiniteQuery(
    kind,
    debouncedQuery,
    ownerScope,
    audiences,
    isOpen
  );

  const ownResults = useMemo(() => {
    const firstPage = searchQuery.data?.pages?.[0];
    const own = firstPage?.data.own ?? [];
    return own.filter((row) => !excludedIdSet.has(Number(row.id)));
  }, [searchQuery.data, excludedIdSet]);

  const globalResults = useMemo(() => {
    const pages = searchQuery.data?.pages ?? [];
    const merged = pages.flatMap((page) => page.data.global);
    return merged.filter((row) => !excludedIdSet.has(Number(row.id)));
  }, [searchQuery.data, excludedIdSet]);

  const kindLabel = kind === "principle" ? "principles" : "tasks";
  const groupYoursLabel = labels.pickerGroupYours.replace("{kind}", kindLabel);
  const groupOthersLabel = labels.pickerGroupOthers.replace("{kind}", kindLabel);
  const noResultsLabel = labels.pickerNoResults.replace("{kind}", kindLabel);
  const createLabel =
    kind === "principle"
      ? labels.pickerCreateNewPrinciple
      : labels.pickerCreateNewTask;
  const effectivePlaceholder =
    placeholder ||
    (kind === "principle"
      ? labels.pickerSearchPlaceholderPrinciple
      : labels.pickerSearchPlaceholderTask);

  const hasResults = ownResults.length > 0 || globalResults.length > 0;
  const isInitialLoading = searchQuery.isLoading && !searchQuery.isFetchingNextPage;

  const handleSelect = (comment: Comment) => {
    setIsOpen(false);
    setRawQuery("");
    setDebouncedQuery("");
    onPick(comment);
  };

  const handleCreate = () => {
    setIsOpen(false);
    const seed = rawQuery.trim();
    setRawQuery("");
    setDebouncedQuery("");
    onCreateNew({ content: seed });
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const distanceFromBottom =
        target.scrollHeight - (target.scrollTop + target.clientHeight);
      if (
        distanceFromBottom <= SCROLL_THRESHOLD_PX &&
        searchQuery.hasNextPage &&
        !searchQuery.isFetchingNextPage &&
        !searchQuery.isLoading
      ) {
        void searchQuery.fetchNextPage();
      }
    },
    [searchQuery]
  );

  const renderPreview = (row: Comment): string => {
    const html = typeof row.content === "string" ? row.content : "";
    const plain = html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/\s+/g, " ")
      .trim();
    if (plain.length <= 140) return plain;
    return `${plain.slice(0, 137)}…`;
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 rounded-full border border-eoe-border-main bg-eoe-bg px-3 py-2 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg">
        <Search className="size-4 shrink-0 text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden />
        <input
          ref={inputRef}
          type="text"
          value={rawQuery}
          onChange={(event) => {
            setRawQuery(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setIsOpen(false);
            }
          }}
          placeholder={effectivePlaceholder}
          className="w-full bg-transparent text-sm text-eoe-text placeholder:text-eoe-text-sub focus:outline-none dark:text-eoe-dark-text dark:placeholder:text-eoe-dark-text-sub"
          role="combobox"
          aria-label={effectivePlaceholder}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls="context-comment-picker-listbox"
        />
      </div>

      {isOpen ? (
        <div
          id="context-comment-picker-listbox"
          role="listbox"
          onScroll={handleScroll}
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-40 max-h-80 overflow-y-auto rounded-lg border border-eoe-border-main bg-eoe-bg shadow-lg dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal"
        >
          {isInitialLoading ? (
            <div className="px-3 py-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {labels.pickerLoading}
            </div>
          ) : null}

          {!isInitialLoading && ownResults.length > 0 ? (
            <div className="py-1">
              <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {groupYoursLabel}
              </div>
              {ownResults.map((row) => (
                <button
                  key={`own-${row.id}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleSelect(row)}
                  className="block w-full cursor-pointer px-3 py-2 text-left text-sm text-eoe-text hover:bg-eoe-bg-hover dark:text-eoe-dark-text dark:hover:bg-eoe-dark-bg-hover"
                >
                  {renderPreview(row)}
                </button>
              ))}
            </div>
          ) : null}

          {!isInitialLoading && globalResults.length > 0 ? (
            <div className="py-1 border-t border-eoe-border-main dark:border-eoe-dark-border-main">
              <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {groupOthersLabel}
              </div>
              {globalResults.map((row) => (
                <button
                  key={`global-${row.id}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleSelect(row)}
                  className="block w-full cursor-pointer px-3 py-2 text-left text-sm text-eoe-text hover:bg-eoe-bg-hover dark:text-eoe-dark-text dark:hover:bg-eoe-dark-bg-hover"
                >
                  {renderPreview(row)}
                  {row.username ? (
                    <div className="mt-0.5 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      @{row.username}
                    </div>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}

          {searchQuery.isFetchingNextPage ? (
            <div className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
              <Loader2 className="size-3 animate-spin" aria-hidden />
              <span>{labels.pickerLoading}</span>
            </div>
          ) : null}

          {!isInitialLoading && !hasResults && debouncedQuery.trim().length > 0 ? (
            <div className="px-3 py-2 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {noResultsLabel}
            </div>
          ) : null}

          {allowCreate ? (
            <button
              type="button"
              onClick={handleCreate}
              className="flex w-full cursor-pointer items-center gap-2 border-t border-eoe-border-main bg-eoe-bg px-3 py-2 text-left text-sm font-semibold text-eoe-text hover:bg-eoe-bg-hover dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg dark:text-eoe-dark-text dark:hover:bg-eoe-dark-bg-hover"
            >
              <Plus className="size-4" aria-hidden />
              <span>{createLabel}</span>
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ContextCommentPicker;
